import { memo } from "react";
import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

/**
 * BrandBackdrop — reusable, additive brand-atmosphere layer.
 *
 * Renders the visual-language brief's signature background layers as opt-in
 * props, all absolutely positioned, behind content (`zIndex: 0`), and
 * `pointerEvents: none`. It is purely decorative — drop it as the first child
 * of a `position: relative` section and give the real content `zIndex: 1`.
 *
 * Calibration is load-bearing (see visual-polish/visual-language-brief.md):
 *   blue ≤ 10%, cyan ≤ 6% (light) / ≤ 18% (navy), indigo ≤ 30% (navy only).
 * Cool band only — no rainbow mesh, no brain/AI tropes, no tech grids, no gold.
 *
 * NOT yet wired into any page. The homepage batch consumes it next.
 */

type Corner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

// Brand-palette anchors (mirror tokens.css / branding) — kept local so the
// component is self-contained and the opacity ceilings are explicit at source.
const PRIMARY = "37,99,235"; // #2563EB primary-500
const CYAN = "6,182,212"; // #06B6D4 brand cyan
const INDIGO = "99,102,241"; // #6366F1 indigo-500
const SKY = "56,189,248"; // #38BDF8 sky-400 — constellation node dots

const MOTIF_SVG: Record<NonNullable<BrandBackdropProps["motif"]>, string> = {
  "orb-halo": "/images/brand/orb-halo.svg",
  "wave-clean": "/images/brand/wave-clean.svg",
  "wave-3layer": "/images/brand/wave-3layer.svg",
};

const CORNER_POSITION: Record<Corner, SxProps<Theme>> = {
  "top-left": { top: 0, left: 0 },
  "top-right": { top: 0, right: 0 },
  "bottom-left": { bottom: 0, left: 0 },
  "bottom-right": { bottom: 0, right: 0 },
};

// Fade-toward-content mask anchored at the placement corner, so a layer reads
// as a quiet corner accent and never competes with type.
const CORNER_MASK: Record<Corner, string> = {
  "top-left": "radial-gradient(120% 100% at 0% 0%, #000 0%, transparent 60%)",
  "top-right": "radial-gradient(120% 100% at 100% 0%, #000 0%, transparent 60%)",
  "bottom-left": "radial-gradient(120% 100% at 0% 100%, #000 0%, transparent 60%)",
  "bottom-right": "radial-gradient(120% 100% at 100% 100%, #000 0%, transparent 60%)",
};

export interface BrandBackdropProps {
  /**
   * Soft cool mesh wash. `"light"` = barely-there blue+cyan glow rising from
   * the top over a near-flat field (default hero). `"navy"` = the night-sky
   * indigo/cyan/blue orb mesh for the deep trust band. `false` (default) = off.
   */
  mesh?: "light" | "navy" | false;
  /**
   * Care Circle constellation dot-field (the SIGNATURE motif). Rendered a touch
   * more present than the prototypes, masked to fade toward content.
   */
  constellation?: boolean;
  /** Corner the constellation fades out from. Default `"top-right"`. */
  constellationCorner?: Corner;
  /** Constellation opacity. Default 0.7 (light) — kept visible-but-tasteful. */
  constellationOpacity?: number;
  /** Render constellation dots for a dark/navy surface (white dots). */
  onNavy?: boolean;
  /** Optional brand-motif SVG layer, recolored via CSS mask-image. */
  motif?: "orb-halo" | "wave-clean" | "wave-3layer";
  /** Corner the motif anchors to. Default `"bottom-right"`. */
  motifCorner?: Corner;
  /** Motif tint as an `r,g,b` string. Default brand primary. */
  motifColor?: string;
  /** Motif opacity. Default 0.5. */
  motifOpacity?: number;
  /** Motif box size in px. Default 460. */
  motifSize?: number;
  /**
   * Slow ambient drift on the motif layer. Guarded by prefers-reduced-motion —
   * the animation is disabled entirely for users who opt out. Default false.
   */
  motifDrift?: boolean;
  /** Escape hatch for the outer container. */
  sx?: SxProps<Theme>;
}

function meshBackground(mesh: "light" | "navy"): string {
  if (mesh === "navy") {
    // Night-sky mesh: indigo top-left (≤30%), cyan top-right (≤18%),
    // blue rising from the bottom (≤20%). Cool band only.
    return [
      `radial-gradient(60% 55% at 15% 20%, rgba(${INDIGO},0.30) 0%, rgba(${INDIGO},0) 60%)`,
      `radial-gradient(55% 50% at 85% 30%, rgba(${CYAN},0.18) 0%, rgba(${CYAN},0) 60%)`,
      `radial-gradient(70% 60% at 50% 110%, rgba(96,165,250,0.20) 0%, rgba(96,165,250,0) 65%)`,
    ].join(", ");
  }
  // Light wash: blue glow rising from above (≤10%) + cyan shoulder (≤6%).
  return [
    `radial-gradient(120% 90% at 50% -10%, rgba(${PRIMARY},0.10) 0%, rgba(${PRIMARY},0.04) 32%, rgba(255,255,255,0) 68%)`,
    `radial-gradient(80% 70% at 85% 0%, rgba(${CYAN},0.06) 0%, rgba(${CYAN},0) 60%)`,
  ].join(", ");
}

function BrandBackdropComponent({
  mesh = false,
  constellation = false,
  constellationCorner = "top-right",
  constellationOpacity = 0.7,
  onNavy = false,
  motif,
  motifCorner = "bottom-right",
  motifColor = PRIMARY,
  motifOpacity = 0.5,
  motifSize = 460,
  motifDrift = false,
  sx,
}: BrandBackdropProps) {
  const dotColor = onNavy ? "rgba(255,255,255,0.10)" : `rgba(${SKY},0.22)`;

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        overflow: "hidden",
        ...sx,
      }}
    >
      {mesh && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: meshBackground(mesh),
          }}
        />
      )}

      {constellation && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: constellationOpacity,
            backgroundImage: `radial-gradient(circle, ${dotColor} 1px, transparent 1.6px)`,
            backgroundSize: "26px 26px",
            WebkitMaskImage: CORNER_MASK[constellationCorner],
            maskImage: CORNER_MASK[constellationCorner],
          }}
        />
      )}

      {motif && (
        <Box
          sx={{
            position: "absolute",
            ...CORNER_POSITION[motifCorner],
            width: motifSize,
            height: motifSize,
            opacity: motifOpacity,
            // Recolor the SVG via mask-image: the source fills are irrelevant,
            // the brand colour comes from backgroundColor showing through.
            backgroundColor: `rgb(${motifColor})`,
            WebkitMaskImage: `url(${MOTIF_SVG[motif]})`,
            maskImage: `url(${MOTIF_SVG[motif]})`,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskSize: "contain",
            maskSize: "contain",
            ...(motifDrift && {
              "@media (prefers-reduced-motion: no-preference)": {
                animation: "brandBackdropDrift 18s ease-in-out infinite alternate",
              },
              "@keyframes brandBackdropDrift": {
                from: { transform: "translate3d(0, 0, 0)" },
                to: { transform: "translate3d(-12px, 8px, 0)" },
              },
            }),
          }}
        />
      )}
    </Box>
  );
}

export const BrandBackdrop = memo(BrandBackdropComponent);
